// Campaign parameters carried by the reorder QR code on printed sticker sheets,
// so a scan shows up in PostHog as its own channel and the session can be tied
// back to the order the sheet was printed for.
//
// Dependency-free on purpose: `proxy.ts` imports it (Edge runtime) to upgrade
// sheets printed before these parameters existed, which carry only
// `ref=reorder`.
//
// Real `utm_*` keys rather than a custom one: PostHog reads them off every
// pageview in every consent state, and `landing-attribution.ts` carries the
// source/medium/campaign trio on to the Shopify order.
//
// THE ORDER MARKER. The UTM chain is first-touch, so a returning customer's
// order reports their original channel, not the scan. The scan therefore also
// travels as its own last-touch marker (`_mk_reorder`): read off the landing
// URL, carried across our own navigations, attached at checkout in every
// consent state (it names one of our orders, not the device), and preferred
// by the operator's "Källa" line. Value: the original order number, or
// `REORDER_QR_UNKNOWN_ORDER` for a sheet printed before the QR carried it.

/**
 * Pre-UTM discriminator (`?ref=reorder`), still written so old and new sheet
 * URLs match. The configurator ignores the key.
 */
export const REORDER_QR_REF_KEY = "ref";
export const REORDER_QR_REF = "reorder";

export const REORDER_QR_UTM = {
  utm_source: "print_sheet",
  utm_medium: "qr",
  utm_campaign: "reorder",
} as const;

/**
 * Tags `searchParams` as a reorder-QR landing. `orderNumber` becomes
 * `utm_content` when known; legacy sheets and dev previews without one get the
 * trio alone.
 */
export function applyReorderQrAttribution(
  searchParams: URLSearchParams,
  orderNumber: number | null,
): void {
  searchParams.set(REORDER_QR_REF_KEY, REORDER_QR_REF);
  for (const [key, value] of Object.entries(REORDER_QR_UTM)) {
    searchParams.set(key, value);
  }
  if (orderNumber !== null) {
    searchParams.set("utm_content", String(orderNumber));
  }
}

/** Query key that hands the marker to the next document across a full load. */
export const REORDER_QR_CARRY_PARAM = "mk_rq";

export const REORDER_QR_UNKNOWN_ORDER = "unknown";

export interface ReorderQrMarker {
  /** The order the scanned sheet was printed for; `null` for legacy sheets. */
  orderNumber: number | null;
}

/**
 * Inverse of `formatReorderQrMarker`. Total: anything else is not a marker,
 * because the value arrives from a URL and ends up in an operator message.
 */
export function parseReorderQrMarker(value: string | null | undefined): ReorderQrMarker | null {
  const trimmed = value?.trim();
  if (trimmed === REORDER_QR_UNKNOWN_ORDER) {
    return { orderNumber: null };
  }
  return trimmed && /^\d{1,12}$/u.test(trimmed) ? { orderNumber: Number(trimmed) } : null;
}

export function formatReorderQrMarker(marker: ReorderQrMarker): string {
  return marker.orderNumber === null ? REORDER_QR_UNKNOWN_ORDER : String(marker.orderNumber);
}

/**
 * The marker a URL carries: a QR landing itself (`ref=reorder`, with the order
 * as `utm_content` when the sheet had it), or one of our own navigations that
 * handed it on via `REORDER_QR_CARRY_PARAM`.
 */
export function readReorderQrMarker(searchParams: URLSearchParams): ReorderQrMarker | null {
  if (searchParams.get(REORDER_QR_REF_KEY) === REORDER_QR_REF) {
    return parseReorderQrMarker(searchParams.get("utm_content")) ?? { orderNumber: null };
  }
  return parseReorderQrMarker(searchParams.get(REORDER_QR_CARRY_PARAM));
}
